// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon, IconName} from '@wandb/weave/components/Icon';
import {clsx} from 'clsx';
import React, {useState} from 'react';

import {Analytics} from '../../../services/analytics';
import {useRunSelectorAnalyticsLocation} from '../RunSelectorAnalyticsLocationContext';

export type MenuItem = {
  icon?: IconName;
  id: string;
  name: string;
  onSelect?: () => void;
  /** A `MenuItem` with `groups` renders a submenu */
  groups?: MenuGroup[];
  isItemIndicatorCheckmark?: boolean;
};

export type MenuGroup = {
  id?: string;
  items: MenuItem[];
  header?: string;
  /** A `MenuGroup` with `value` renders a radio group */
  value?: string;
  onValueChange?: (newValue: string) => void;
};

export type CustomMenuGroup = {
  id: string;
  items: [];
  header?: string;
  type: 'custom';
  render: () => React.ReactNode;
};

export type WBTableCellColumnHeaderMenuProps = {
  columnDisplayName: string;
  groups: MenuGroup[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

const Item = (props: MenuItem) => {
  const {onSelect, icon, name, groups} = props;
  const isSubmenu = !!groups?.length;
  if (isSubmenu) {
    return <Submenu {...props} />;
  }
  return (
    <DropdownMenu.Item onSelect={onSelect}>
      {icon && <Icon name={icon} />}
      {name}
    </DropdownMenu.Item>
  );
};

const RadioItem = ({id, icon, name, isItemIndicatorCheckmark}: MenuItem) => {
  return (
    <DropdownMenu.RadioItem value={id}>
      {icon && <Icon name={icon} />}
      {name}
      {isItemIndicatorCheckmark ? (
        <DropdownMenu.ItemIndicatorCheckmark />
      ) : (
        <DropdownMenu.ItemIndicatorRadio />
      )}
    </DropdownMenu.RadioItem>
  );
};

const Submenu = ({icon, name, groups}: MenuItem) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <DropdownMenu.Sub open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.SubTrigger>
        {icon && <Icon name={icon} />}
        {name}
        <Icon name="chevron-next" className="ml-auto text-moon-450" />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent className="z-[200] w-[8rem]">
          {groups?.map((group, idx) => (
            <Group key={idx} idx={idx} {...group} />
          ))}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
};

export function isCustomMenuGroup(
  group: MenuGroup | CustomMenuGroup
): group is CustomMenuGroup {
  return 'type' in group;
}

export function isMenuGroup(
  group: MenuGroup | CustomMenuGroup
): group is MenuGroup {
  return !('type' in group);
}

const CustomGroup = ({
  header,
  idx,
  render,
}: CustomMenuGroup & {idx: number}) => (
  <>
    {idx > 0 && <DropdownMenu.Separator />}
    <DropdownMenu.Group aria-label={header}>
      {header && (
        <DropdownMenu.Label className="mx-6 px-10 py-6 font-semibold">
          {header}
        </DropdownMenu.Label>
      )}
      {render()}
    </DropdownMenu.Group>
  </>
);

const Group = ({
  header,
  idx,
  items,
  onValueChange,
  value,
}: MenuGroup & {idx: number}) => {
  const isRadioGroup = value !== undefined;
  const Group = isRadioGroup ? DropdownMenu.RadioGroup : DropdownMenu.Group;

  return (
    <>
      {idx > 0 && <DropdownMenu.Separator />}
      <Group
        aria-label={header}
        value={(isRadioGroup ? value : undefined)!}
        onValueChange={onValueChange}>
        {header && (
          <DropdownMenu.Label className="mx-6 px-10 py-6 font-semibold">
            {header}
          </DropdownMenu.Label>
        )}
        {isRadioGroup
          ? items.map(item => <RadioItem key={item.id} {...item} />)
          : items.map(item => <Item key={item.id} {...item} />)}
      </Group>
    </>
  );
};

export const WBTableCellColumnHeaderMenu = ({
  columnDisplayName,
  groups,
  isOpen,
  setIsOpen,
}: WBTableCellColumnHeaderMenuProps) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger>
        <Button
          icon="overflow-horizontal"
          variant="ghost"
          aria-label={`${columnDisplayName} column actions`}
          active={isOpen}
          className={clsx('column-actions-trigger mr-8', {
            '!opacity-100': isOpen,
          })}
          onClick={() => {
            Analytics.track('Column header dropdown clicked', {
              location: analyticsLocation,
            });
          }}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" className="z-[200]">
          {groups.map((group, idx) => {
            if (isCustomMenuGroup(group)) {
              return <CustomGroup key={group.id} idx={idx} {...group} />;
            }
            return <Group key={group.value ?? group.id} idx={idx} {...group} />;
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
